import React, { useState, useEffect, forwardRef } from "react";
import cx from "classnames";
import { Button } from "react-bootstrap";
import styled from "styled-components";
import InputTextarea from "../Form/InputTextarea";
import InputText from "../Form/InputText";
import InputDropdown from "../Form/InputDropdown";
import OptionDropdown from "../Form/InputDropdown/OptionDropdown";
import useQueryParam from "../../utilities/useQueryParam";
import { getContactTopics, submitContactRequest } from "../../services/ContactService";
import ReCaptcha from "../ReCaptcha";
import IEditableProps from "../../utilities/IEditableProps";
import IStyledProps from "../../utilities/IStyledProps";

const ContactFormContainer = styled.div`

    h1, h2 {
        color: ${props => props.theme.primary};
    }

    // If there is a text color class it applies it to the title
    &[class*="text-"] {
        h1, h2 {
            color: inherit;
        }
    }

    @media(max-width:992px){
        max-width:100% !important;

        button{
            padding-left: 15px;
            padding-right: 15px;
            width: 100%;
            height: 50px;
        }
    }

    .form-group, .error-message  {
        max-width: 700px;
    }
`;

export interface IContactFormProps extends IEditableProps, IStyledProps {
    backgroundSrc: string;
    backgroundAlt: string;
    adobeWait: string;
    placeholderSrc: string;
    children;
}

export default forwardRef(({cmsId,
    editorClassObj,
    className,
}: IContactFormProps, ref: any) => {
    const isWhite = className && className.includes('light-inputs');

    // Url params
    const [urlName] = useQueryParam('name', '');
    const [urlEmail] = useQueryParam('email', '');
    const [urlTopic] = useQueryParam('topic', '');
    const [urlSubtopic] = useQueryParam('subtopic', '');
    const [urlQuestion] = useQueryParam('question', '');

    const [selectedTopic, setSelectedTopic] = useState(urlTopic);
    const [contactTopics, setContactTopics] = useState([]);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [captchaValue, setCaptchaValue] = useState('');

    useEffect(() => {
        getAndSetContactTopics();
    }, []);

    function getSubTopics(topic) {
        return contactTopics.find(({ name }) => topic === name)?.subTopics || [];
    }

    async function getAndSetContactTopics() {
        let topics: any = [];
        try {
            topics = await getContactTopics();
        } catch (error) {
            console.error(error);
        }
        setContactTopics(topics);
    }

    function handleRecaptchaChange(value) {
       setCaptchaValue(value);
    }

    async function handleContactSubmit(e) {
        e.preventDefault();
        setSubmitError(false);
        const formElements = e.target.elements;

        const body = {
            message: formElements.question.value,
            name: formElements.name.value,
            emailFrom: formElements.email.value,
            subject: `${formElements.topic.value} - ${formElements.subtopic.value}`,
            reCaptchaResponse: captchaValue,
        };

        const success = await submitContactRequest(body);

        if (success) {
            setSubmitSuccess(true);
        } else {
            setSubmitError(true);
        }
    }

    function handleTopicChange(selectedTopic) {
        setSelectedTopic(selectedTopic);
    }

    return (
        <ContactFormContainer data-id={cmsId} ref={ref}  className={cx(className, editorClassObj, 'container py-6 form-custom')}>
            <form onSubmit={handleContactSubmit} className="contact-form">
                <div className="px-3">
                    <p>For security reasons, we are unable to discuss account details via email. If you need help with your account, please contact us at 1-800-748-4302.</p>
                    <h2 className={cx("text-serif title")}>{submitSuccess ? 'Thank you for submitting your question' : 'Submit a question to our support team'}</h2>
                    <h6 className="text-sans font-weight-bold">Our dedicated staff will respond within 48 hours.</h6>
                </div>
                {!submitSuccess &&
                    <div className="px-3">
                        <div className="form-group">
                            <InputText inputRequired={true} inputLabel="Name" inputFieldName='name' inputDefaultValue={urlName} />
                        </div>
                        <div className="form-group">
                            <InputText inputRequired={true} inputLabel="Email" inputFieldName='email' inputType='email' inputDefaultValue={urlEmail} isWhite={isWhite}/>
                        </div>
                        <InputDropdown id="Topic" name="topic" title='Topic' onChange={handleTopicChange} required={true} isWhite={isWhite}>
                            {contactTopics.map((option => {
                                return <OptionDropdown eventKey={option.name} text={option.name} />
                            }))}
                        </InputDropdown>
                        <InputDropdown id="SubTopic" name="subtopic" title='SubTopic' required={true} autoSelect={true} isWhite={isWhite}>
                            {getSubTopics(selectedTopic).map((option => {
                                return <OptionDropdown text={option.name} selected={option.name === urlSubtopic}/>
                            }))}
                        </InputDropdown>
                        <InputTextarea inputLabel="Question" inputFieldName="question" inputDefaultValue={urlQuestion} inputRequired={true} isWhite={isWhite} className="mt-4"/>
                        <div className="form-group">
                            <ReCaptcha onChange={handleRecaptchaChange} />
                        </div>
                        <div>
                            <Button disabled={!Boolean(captchaValue)} className="btn inverse contactSubmit" type="submit">Submit</Button>
                        </div>
                        {submitError &&
                            <div className="error-message py-4 text-primary">
                                We're sorry, we could not process your request at this time. Please try again later or contact us via phone.
                            </div>
                        }
                    </div>
                }
            </form>
        </ContactFormContainer >
    );
});